import { __assign } from "tslib";
import { addClass, removeClass, getShadowElement } from '@tyler-components-web/core';
import { MDCRipple, MDCRippleFoundation } from '@material/ripple';
import { applyPassive } from '@material/dom/events';
import { matchesSelectors } from 'shadow-utils';
import { SWITCH_CONSTANTS } from './switch-constants';
var SwitchAdapter = /** @class */ (function () {
    function SwitchAdapter(_component) {
        this._component = _component;
        this._rootElement = getShadowElement(this._component, SWITCH_CONSTANTS.selectors.ROOT);
    }
    Object.defineProperty(SwitchAdapter.prototype, "root_", {
        get: function () {
            return this._rootElement;
        },
        enumerable: true,
        configurable: true
    });
    SwitchAdapter.prototype.initialize = function () {
        this._configureElements();
        this._attachInternalInputListeners();
        this._ripple = this._createRipple();
    };
    SwitchAdapter.prototype._configureElements = function () {
        this._inputElement = this._component.querySelector(SWITCH_CONSTANTS.selectors.INPUT);
        this._labelElement = this._component.querySelector(SWITCH_CONSTANTS.selectors.LABEL);
        if (this._labelElement) {
            this._labelElement.setAttribute(SWITCH_CONSTANTS.attributes.SLOT, SWITCH_CONSTANTS.selectors.LABEL);
        }
        if (this._inputElement) {
            this._inputElement.setAttribute(SWITCH_CONSTANTS.attributes.SLOT, SWITCH_CONSTANTS.selectors.INPUT);
            this._inputElement.setAttribute('role', 'switch');
        }
        addClass(SWITCH_CONSTANTS.classes.UPGRADED, this._rootElement);
    };
    SwitchAdapter.prototype.disconnect = function () {
        if (this._labelElement) {
            this._labelElement.removeAttribute(SWITCH_CONSTANTS.attributes.SLOT);
        }
        if (this._inputElement) {
            this._inputElement.removeAttribute(SWITCH_CONSTANTS.attributes.SLOT);
        }
        if (this._inputMutationObserver) {
            this._inputMutationObserver.disconnect();
        }
        this._detachInternalInputListeners();
        if (this._ripple) {
            this._ripple.destroy();
        }
    };
    SwitchAdapter.prototype.setDense = function (value) {
        if (value) {
            this.setHostAttribute(SWITCH_CONSTANTS.attributes.DENSE);
            this.setRootClass(SWITCH_CONSTANTS.classes.DENSE);
        }
        else {
            this.removeHostAttribute(SWITCH_CONSTANTS.attributes.DENSE);
            this.removeRootClass(SWITCH_CONSTANTS.classes.DENSE);
        }
        // re-layout the ripple for cases where dense was changed after initial layout
        if (this._ripple) {
            this._ripple.layout();
        }
    };
    SwitchAdapter.prototype.isDisabled = function () {
        return !!this._inputElement && this._inputElement.hasAttribute('disabled') && this._inputElement.getAttribute('disabled') !== 'false';
    };
    SwitchAdapter.prototype.isChecked = function () {
        return !!this._inputElement && this._inputElement.checked;
    };
    SwitchAdapter.prototype.setHostAttribute = function (name) {
        this._component.setAttribute(name, '');
    };
    SwitchAdapter.prototype.removeHostAttribute = function (name) {
        this._component.removeAttribute(name);
    };
    SwitchAdapter.prototype.setRootClass = function (classes) {
        if (this._rootElement) {
            addClass(classes, this._rootElement);
        }
    };
    SwitchAdapter.prototype.removeRootClass = function (classes) {
        if (this._rootElement) {
            removeClass(classes, this._rootElement);
        }
    };
    SwitchAdapter.prototype.setNativeAttribute = function (qualifiedName, value) {
        this._inputElement.setAttribute(qualifiedName, value);
    };
    SwitchAdapter.prototype.removeNativeAttribute = function (qualifiedName) {
        this._inputElement.removeAttribute(qualifiedName);
    };
    SwitchAdapter.prototype.setInputAttributeObserver = function (listener) {
        var _this = this;
        this._inputMutationObserver = new MutationObserver(function (mutations) {
            for (var _i = 0, mutations_1 = mutations; _i < mutations_1.length; _i++) {
                var mutation = mutations_1[_i];
                if (mutation.attributeName) {
                    listener(mutation.attributeName, _this._inputElement.getAttribute(mutation.attributeName));
                }
            }
        });
        this._inputMutationObserver.observe(this._inputElement, {
            attributes: true,
            attributeFilter: ['disabled']
        });
    };
    SwitchAdapter.prototype._attachInternalInputListeners = function () {
        var _this = this;
        this._inputFocusHandler = function () { return _this._handleInputFocusListener(); };
        this._inputBlurHandler = function () { return _this._handleInputBlurListener(); };
        this._inputElement.addEventListener('focus', this._inputFocusHandler);
        this._inputElement.addEventListener('blur', this._inputBlurHandler);
    };
    SwitchAdapter.prototype._detachInternalInputListeners = function () {
        removeEventListener('focus', this._inputFocusHandler);
        removeEventListener('blur', this._inputBlurHandler);
    };
    SwitchAdapter.prototype._validDescriptor = function (inputPropDesc) {
        return !!inputPropDesc && typeof inputPropDesc.set === 'function';
    };
    SwitchAdapter.prototype.listen = function (qualifiedName, callback, onRoot) {
        if (onRoot === void 0) { onRoot = false; }
        if (!this._inputElement) {
            return;
        }
        if (onRoot) {
            this._rootElement.addEventListener(qualifiedName, callback);
        }
        else {
            this._inputElement.addEventListener(qualifiedName, callback);
        }
    };
    SwitchAdapter.prototype.unlisten = function (qualifiedName, callback, onRoot) {
        if (onRoot === void 0) { onRoot = false; }
        if (!this._inputElement) {
            return;
        }
        if (onRoot) {
            this._rootElement.removeEventListener(qualifiedName, callback);
        }
        else {
            this._inputElement.removeEventListener(qualifiedName, callback);
        }
    };
    SwitchAdapter.prototype.installPropertyChangeHooks = function (callback) {
        var _this = this;
        var nativeCb = this._inputElement;
        var cbProto = Object.getPrototypeOf(nativeCb);
        SWITCH_CONSTANTS.CB_PROTO_PROPS.forEach(function (controlState) {
            var desc = Object.getOwnPropertyDescriptor(cbProto, controlState);
            // We have to check for this descriptor, since some browsers (Safari) don't support its return.
            // See: https://bugs.webkit.org/show_bug.cgi?id=49739
            if (!_this._validDescriptor(desc)) {
                return;
            }
            // Type cast is needed for compatibility with Closure Compiler.
            var nativeGetter = desc.get;
            if (desc) {
                var nativeCbDesc = {
                    configurable: desc.configurable,
                    enumerable: desc.enumerable,
                    get: nativeGetter,
                    set: function (state) {
                        desc.set.call(nativeCb, state);
                        callback();
                    }
                };
                Object.defineProperty(nativeCb, controlState, nativeCbDesc);
            }
        });
    };
    SwitchAdapter.prototype.uninstallPropertyChangeHooks = function () {
        var _this = this;
        if (!this._inputElement) {
            return;
        }
        var nativeCb = this._inputElement;
        var cbProto = Object.getPrototypeOf(nativeCb);
        SWITCH_CONSTANTS.CB_PROTO_PROPS.forEach(function (controlState) {
            var desc = Object.getOwnPropertyDescriptor(cbProto, controlState);
            if (!_this._validDescriptor(desc)) {
                return;
            }
            Object.defineProperty(nativeCb, controlState, desc);
        });
    };
    SwitchAdapter.prototype._handleInputFocusListener = function () {
        addClass(SWITCH_CONSTANTS.classes.FOCUSED, this._rootElement);
    };
    SwitchAdapter.prototype._handleInputBlurListener = function () {
        removeClass(SWITCH_CONSTANTS.classes.FOCUSED, this._rootElement);
    };
    /** Creates an instance of the MDCRipple class */
    SwitchAdapter.prototype._createRipple = function () {
        var _this = this;
        var RIPPLE_SURFACE_SELECTOR = SWITCH_CONSTANTS.strings.RIPPLE_SURFACE_SELECTOR;
        var rippleSurface = this.root_.querySelector(RIPPLE_SURFACE_SELECTOR);
        // DO NOT INLINE this variable. For backward compatibility, foundations take a Partial<MDCFooAdapter>.
        // To ensure we don't accidentally omit any methods, we need a separate, strongly typed adapter variable.
        var adapter = __assign(__assign({}, MDCRipple.createAdapter(this)), { addClass: function (className) { return rippleSurface.classList.add(className); }, computeBoundingRect: function () { return rippleSurface.getBoundingClientRect(); }, deregisterInteractionHandler: function (evtType, handler) {
                _this._inputElement.removeEventListener(evtType, handler, applyPassive());
            }, isSurfaceActive: function () { return matchesSelectors(_this._inputElement, ':active'); }, isUnbounded: function () { return true; }, registerInteractionHandler: function (evtType, handler) {
                _this._inputElement.addEventListener(evtType, handler, applyPassive());
            }, removeClass: function (className) { return rippleSurface.classList.remove(className); }, updateCssVariable: function (varName, value) {
                rippleSurface.style.setProperty(varName, value);
            } });
        return new MDCRipple(this.root_, new MDCRippleFoundation(adapter));
    };
    return SwitchAdapter;
}());
export { SwitchAdapter };
